package se.mittpass.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.AbsenceRequestDto
import se.mittpass.shared.api.StaffMemberDto
import se.mittpass.web.*

@Composable
fun MobileSemesterView(
    currentYear: Int,
    currentMonth: Int,
    staff: List<StaffMemberDto>,
    absences: List<AbsenceRequestDto>,
    isManager: Boolean,
    currentUserId: Long?,
    onAbsenceClick: (AbsenceRequestDto) -> Unit,
    onAddVacation: () -> Unit
) {
    val monthPrefix = "$currentYear-${currentMonth.toString().padStart(2, '0')}"
    val monthAbsences = remember(absences, monthPrefix) {
        absences.filter {
            it.type == "vacation" &&
            (it.startDate.startsWith(monthPrefix) || it.endDate.startsWith(monthPrefix) ||
             (it.startDate <= "$monthPrefix-01" && it.endDate >= "$monthPrefix-28"))
        }.sortedBy { it.startDate }
    }

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1rem")
            property("width", "100%")
        }
    }) {
        // Quick Action Bar
        Div({
            style {
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "16px")
                property("padding", "0.85rem 1rem")
            }
        }) {
            Div {
                H4({ style { property("margin", "0"); property("font-size", "0.95rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Semesteransökningar")
                }
                Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                    Text("${monthAbsences.size} registrerade denna månad")
                }
            }

            Button({
                onClick { onAddVacation() }
                style {
                    property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                    property("color", "#FFFFFF")
                    property("border", "none")
                    property("border-radius", "50px")
                    property("padding", "0.45rem 0.9rem")
                    property("font-size", "0.8rem")
                    property("font-weight", "700")
                    property("cursor", "pointer")
                    property("box-shadow", "0 2px 8px rgba(108,60,225,0.25)")
                }
            }) {
                Text("+ Ansök")
            }
        }

        if (monthAbsences.isEmpty()) {
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border", "1px dashed var(--rp-hl-med)")
                    property("border-radius", "16px")
                    property("padding", "3rem 1.5rem")
                    property("text-align", "center")
                    property("color", "var(--rp-muted)")
                }
            }) {
                FaIcon("fa-solid fa-umbrella-beach", size = "2rem", color = "var(--rp-muted)")
                Span({
                    style {
                        property("display", "block")
                        property("font-weight", "700")
                        property("color", "var(--rp-text)")
                        property("margin", "0.75rem 0 0.25rem")
                        property("font-size", "0.95rem")
                    }
                }) { Text("Inga semestrar planerade") }
                Span({ style { property("font-size", "0.82rem") } }) { Text("Ingen har registrerat semester för denna månad.") }
            }
        } else {
            Div({
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "0.6rem")
                }
            }) {
                monthAbsences.forEach { abs ->
                    val member = staff.find { it.userId == abs.userId }
                    val memberName = member?.let { "${it.firstName ?: ""} ${it.lastName ?: ""}".trim().ifEmpty { it.email } } ?: "Medarbetare"
                    val initials = memberName.split(" ").mapNotNull { it.take(1).ifEmpty { null } }.joinToString("").take(2).uppercase().ifEmpty { "MP" }
                    val isApproved = abs.status == "approved"
                    val isPending = abs.status == "pending"

                    Div({
                        onClick { onAbsenceClick(abs) }
                        style {
                            property("background", "var(--rp-surface)")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "16px")
                            property("padding", "0.95rem 1rem")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "space-between")
                            property("cursor", "pointer")
                            property("box-shadow", "0 1px 3px rgba(13,13,24,0.03)")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                            Div({
                                style {
                                    property("width", "38px")
                                    property("height", "38px")
                                    property("border-radius", "50%")
                                    property("background", if (isApproved) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                                    property("color", if (isApproved) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                                    property("font-weight", "800")
                                    property("font-size", "0.8rem")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                }
                            }) { Text(initials) }

                            Div {
                                Span({ style { property("font-size", "0.92rem"); property("font-weight", "700"); property("color", "var(--rp-text)"); property("display", "block") } }) {
                                    Text(memberName)
                                }
                                Span({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600"); property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                                    FaIcon("fa-regular fa-calendar", size = "0.75rem", color = "var(--rp-subtle)")
                                    Text("${abs.startDate} → ${abs.endDate}")
                                }
                                if (!abs.note.isNullOrBlank()) {
                                    Span({ style { property("font-size", "0.72rem"); property("color", "var(--rp-muted)"); property("display", "flex"); property("align-items", "center"); property("gap", "0.3rem"); property("margin-top", "2px") } }) {
                                        FaIcon("fa-regular fa-comment", size = "0.7rem", color = "var(--rp-muted)")
                                        Text("\"${abs.note}\"")
                                    }
                                }
                            }
                        }

                        Span({
                            style {
                                property("font-size", "0.75rem")
                                property("font-weight", "800")
                                property("padding", "0.25rem 0.65rem")
                                property("border-radius", "50px")
                                property("background", if (isApproved) "var(--rp-ok-bg)" else if (isPending) "var(--rp-warn-bg)" else "var(--rp-err-bg)")
                                property("color", if (isApproved) "var(--rp-ok-text)" else if (isPending) "var(--rp-warn-text)" else "var(--rp-err-text)")
                            }
                        }) {
                            Text(if (isApproved) "Godkänd" else if (isPending) "Väntar" else "Avslagen")
                        }
                    }
                }
            }
        }
    }
}
